import type { ComponentChildren } from "preact";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { usePortalContainer } from "@/context/PortalContext";
import { X } from "lucide-react";

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  const container = usePortalContainer();
  return (
    <DialogPrimitive.Portal
      data-slot="dialog-portal"
      container={container}
      {...props}
    />
  );
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  onClick,
  ...props
}: DialogPrimitive.Backdrop.Props & {
  onClick?: () => void;
}) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      forceRender
      className={cn(
        "tw:data-open:animate-in tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-open:fade-in-0 tw:bg-black/30 tw:duration-200 tw:supports-backdrop-filter:backdrop-blur-xs tw:fixed tw:inset-0 tw:z-50",
        className,
      )}
      onClick={onClick}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onOpenChange,
  overlayClassName,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Merged into backdrop (e.g. higher z-index when dialog opens inside a sheet). */
  overlayClassName?: string;
}) {
  return (
    <DialogPortal>
      <DialogOverlay
        className={overlayClassName}
        onClick={() => onOpenChange?.(false)}
      />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "tw:bg-white tw:data-open:animate-in tw:data-closed:fade-out-0 tw:data-open:fade-in-0 tw:data-open:zoom-in-95 tw:data-closed:zoom-out-95 tw:shadow-xl tw:rounded-xl tw:p-6 tw:text-sm tw:duration-300 tw:ease-out tw:fixed tw:z-50 tw:outline-none tw:max-w-lg tw:w-[calc(100%-2rem)] tw:left-1/2 tw:top-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:cursor-default tw:border tw:border-gray-200",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <button
                className="tw:absolute tw:top-3 tw:right-3 tw:text-black tw:cursor-pointer tw:hover:opacity-70 tw:transition-opacity tw:z-10 tw:bg-transparent tw:border-none tw:p-0"
                aria-label="Close"
              />
            }
          >
            <X className="tw:size-4" />
            <span className="tw:sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  ...props
}: {
  className?: string;
  children: ComponentChildren;
}) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("tw:gap-1 tw:flex tw:flex-col tw:pb-2.5", className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: {
  className?: string;
  showCloseButton?: boolean;
  children: ComponentChildren;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "tw:gap-2 tw:flex tw:flex-col-reverse tw:sm:flex-row tw:sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({
  className,
  ...props
}: DialogPrimitive.Title.Props & { className?: string }) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("tw:text-base tw:font-semibold tw:leading-snug", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props & { className?: string }) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "tw:text-muted-foreground tw:[*]:hover:text-foreground tw:text-sm tw:[*]:underline tw:[*]:underline-offset-3",
        className
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
